import { useEffect, useState } from "react";
import type { Appointment, ProcedureCode } from "@dentacore/shared-types";

import { listProcedureCodes } from "../api/catalogue";
import { formatMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { useBookingScoped } from "./booking-location";
import { procedureIds, visitMembers } from "./group-visits";
import styles from "./family-bill.module.css";

/**
 * **What the bill for this visit will be** (FV-D14, Emile, 2026-10-22: *"show me
 * how a bill would look like for this person, an invoice"*).
 *
 * One section per patient — each keeps their own bill, because each has their
 * own file — then what the family pays together at the desk. Priced from the
 * location's catalogue as booked; a draft until the visit is completed, when
 * these lines are what completing it bills.
 */
export function FamilyBill({
  appointment,
  currency,
  where,
  timezone,
  onClose,
}: {
  appointment: Appointment;
  currency: string;
  where: string | null;
  timezone: string;
  onClose: () => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const [catalogue, setCatalogue] = useState<ProcedureCode[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    here(listProcedureCodes)(false, controller.signal)
      .then(setCatalogue)
      .catch(() => setCatalogue([]));
    return () => controller.abort();
  }, [here]);

  // Everyone the booking stands for, each with every procedure they come for (FV-D6).
  const people = visitMembers(appointment).flatMap((member) =>
    member.patient === null ? [] : [{ patient: member.patient, ids: procedureIds(member) }],
  );
  const lines = (ids: readonly string[]) =>
    ids.map((id) => {
      const code = catalogue?.find((row) => row.id === id);
      return { id, label: code?.label ?? "Procedure", cents: code?.defaultPriceCents ?? 0 };
    });
  const subtotal = (ids: readonly string[]) =>
    lines(ids).reduce((sum, line) => sum + line.cents, 0);
  const total = people.reduce((sum, person) => sum + subtotal(person.ids), 0);
  const when = new Date(appointment.startsAt).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: timezone,
  });

  return (
    <Modal
      label="Bill for this visit"
      onClose={onClose}
      testId="family-bill"
      panelClassName={styles.panel}
    >
      <div className={styles.head}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>Bill</h2>
          <span className={styles.draft}>Draft · not issued</span>
        </div>
        <span className={styles.meta}>
          {[where, when, people.length === 1 ? null : `${String(people.length)} patients`]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      {catalogue === null ? (
        <p className={styles.meta}>Reading the prices…</p>
      ) : (
        <div className={styles.body}>
          {people.map((person) => (
            <section
              key={person.patient.id}
              className={styles.person}
              data-testid="family-bill-person"
            >
              <div className={styles.personHead}>
                <span className={styles.name}>
                  {person.patient.firstName} {person.patient.lastName}
                </span>
                <span className={styles.file}>{person.patient.fileNumberDisplay}</span>
              </div>
              {person.ids.length === 0 ? (
                <p className={styles.none}>Nothing decided yet — nothing billed.</p>
              ) : (
                lines(person.ids).map((line) => (
                  <div key={line.id} className={styles.line}>
                    <span>{line.label}</span>
                    <span className={styles.money}>{formatMoney(line.cents, currency)}</span>
                  </div>
                ))
              )}
              {/* A subtotal only means something beside other people's. */}
              {people.length > 1 && (
                <div className={styles.subtotal}>
                  <span>Bill for {person.patient.firstName}</span>
                  <span className={styles.money}>
                    {formatMoney(subtotal(person.ids), currency)}
                  </span>
                </div>
              )}
            </section>
          ))}

          <div className={styles.total} data-testid="family-bill-total">
            <span>{people.length === 1 ? "To pay" : "To pay together"}</span>
            <span className={styles.money}>{formatMoney(total, currency)}</span>
          </div>
          <p className={styles.note}>
            {people.length === 1
              ? "Completing the visit adds these lines to the patient's bill."
              : "Each patient keeps their own bill on their file. Completing the visit adds these lines; a payment can be taken for all of them at once."}
          </p>
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.close} onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
